import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import WikiIcon from '../shared/WikiIcon';
import { WIKI_MODAL_COPY } from './wikiModalCopy';
import {
    clampHighlight,
    computePickerPosition,
    filterWikiLocations,
    moveHighlight,
    pickerPopoverStyle,
    pickerRows,
} from './wikiLocations';

const viewportHeight = () => (typeof window !== 'undefined' ? window.innerHeight : 800);

/**
 * State for the New page modal's floating location picker (C2 §1.6). The
 * trigger lives in the card body; the popover is rendered by the modal shell
 * as a SIBLING of the card (`beforeCard`) — the card clips its overflow, and
 * the prototype's transparent click-catcher must cover the card too.
 *
 *   open      position computed once from the trigger rect (flip up when
 *             < 280px below and more room above; 160–400px tall), query
 *             cleared, highlight on the current location, filter focused
 *   keys      Esc closes the picker only · ↑/↓ move (clamped) · Enter or
 *             Tab pick the highlighted row · Enter with no match: nothing
 *   click     a row picks it; the click-catcher closes without a change
 *
 * Real-build additions: the position is re-measured on window resize (the
 * prototype kept a stale snapshot), focus returns to the trigger after a pick
 * or Esc, and the list is an ARIA combobox/listbox.
 */
export function useWikiLocationPicker({ locations, currentId, onPick }) {
    const triggerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const onPickRef = useRef(onPick);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [hi, setHi] = useState(0);
    const [pos, setPos] = useState(null);

    useEffect(() => { onPickRef.current = onPick; }, [onPick]);

    const rows = useMemo(
        () => (open ? pickerRows(locations, query, currentId, hi) : []),
        [open, locations, query, currentId, hi],
    );
    const hiIndex = clampHighlight(hi, rows.length);

    const measure = useCallback(() => {
        const el = triggerRef.current;
        const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
        return computePickerPosition(rect, viewportHeight());
    }, []);

    const close = useCallback(({ restoreFocus = false } = {}) => {
        setOpen(false);
        setQuery('');
        if (restoreFocus && triggerRef.current) triggerRef.current.focus({ preventScroll: true });
    }, []);

    const openPicker = useCallback(() => {
        const current = (locations || []).findIndex((loc) => loc.id === currentId);
        setPos(measure());
        setQuery('');
        setHi(Math.max(0, current));
        setOpen(true);
    }, [locations, currentId, measure]);

    const toggle = useCallback(() => {
        if (open) close();
        else openPicker();
    }, [open, close, openPicker]);

    const pick = useCallback((id) => {
        if (onPickRef.current) onPickRef.current(id);
        close({ restoreFocus: true });
    }, [close]);

    // Focus the filter once the popover is in the DOM (prototype: 20ms later,
    // preventScroll) and keep the highlighted row in view.
    useEffect(() => {
        if (!open || !inputRef.current) return;
        inputRef.current.focus({ preventScroll: true });
    }, [open]);

    useEffect(() => {
        if (!open || !listRef.current) return;
        const row = listRef.current.querySelector('[data-hi="1"]');
        if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
    }, [open, hiIndex, query]);

    useEffect(() => {
        if (!open || typeof window === 'undefined') return undefined;
        const onResize = () => setPos(measure());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [open, measure]);

    const onQuery = useCallback((event) => {
        setQuery(event.target.value);
        setHi(0);
    }, []);

    const onKeyDown = useCallback((event) => {
        const list = filterWikiLocations(locations, query);
        const current = clampHighlight(hi, list.length);
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close({ restoreFocus: true });
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setHi(moveHighlight(current, event.key === 'ArrowDown' ? 1 : -1, list.length));
            return;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
            const row = list[current];
            if (!row) return;
            event.preventDefault();
            pick(row.id);
        }
    }, [locations, query, hi, close, pick]);

    return {
        open, query, rows, hiIndex, pos,
        triggerRef, inputRef, listRef,
        toggle, close, pick, onQuery, onKeyDown,
    };
}

/** The LOCATION trigger: folder · "Cat › Sub" · chevron, teal ring while open. */
export function WikiLocationTrigger({ picker, label, listboxId, testId = 'wiki-location-trigger' }) {
    const empty = !label;
    return (
        <button
            ref={picker.triggerRef}
            type="button"
            className={`wk-loc-trigger${picker.open ? ' is-open' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={picker.open}
            aria-controls={picker.open ? listboxId : undefined}
            data-testid={testId}
            data-field="wiki.page_location"
            onClick={picker.toggle}
        >
            <WikiIcon name="folder" size={15} />
            <span className={`wk-loc-trigger-label${empty ? ' is-empty' : ''}`}>
                {label || WIKI_MODAL_COPY.pickerNoLocation}
            </span>
            <WikiIcon name={picker.open ? 'chevron-up' : 'chevron-down'} size={15} />
        </button>
    );
}

/**
 * The popover layer: a transparent full-page click-catcher + the fixed
 * popover (filter, list with `overscroll-behavior: contain`, empty message,
 * key hint). Render it as the modal shell's `beforeCard`.
 */
export function WikiLocationLayer({ picker, listboxId }) {
    const baseId = useId();
    if (!picker.open) return null;
    const { rows, hiIndex } = picker;
    const activeId = rows.length ? `${baseId}-opt-${hiIndex}` : undefined;
    return (
        <>
            <div
                className="wk-loc-catcher"
                data-testid="wiki-location-catcher"
                aria-hidden="true"
                onClick={() => picker.close()}
            />
            <div className="wk-loc-pop" style={pickerPopoverStyle(picker.pos)} data-testid="wiki-location-popover" data-placement={picker.pos && !picker.pos.down ? 'up' : 'down'}>
                <div className="wk-loc-filter">
                    <WikiIcon name="search" size={14} />
                    <input
                        ref={picker.inputRef}
                        className="wk-loc-input"
                        value={picker.query}
                        onChange={picker.onQuery}
                        onKeyDown={picker.onKeyDown}
                        placeholder={WIKI_MODAL_COPY.pickerPlaceholder}
                        role="combobox"
                        aria-expanded="true"
                        aria-controls={listboxId}
                        aria-activedescendant={activeId}
                        aria-autocomplete="list"
                        aria-label={WIKI_MODAL_COPY.pickerPlaceholder}
                        autoComplete="off"
                        spellCheck={false}
                        data-testid="wiki-location-filter"
                    />
                </div>
                <ul ref={picker.listRef} id={listboxId} className="wk-loc-list" role="listbox" data-testid="wiki-location-list">
                    {rows.map((row, index) => {
                        const cls = [
                            'wk-loc-row',
                            row.isSub ? '' : 'wk-loc-row--cat',
                            row.indent ? 'wk-loc-row--indent' : '',
                            row.on ? 'is-current' : '',
                            row.isHi ? 'is-hi' : '',
                        ].filter(Boolean).join(' ');
                        return (
                            <li
                                key={row.id}
                                id={`${baseId}-opt-${index}`}
                                role="option"
                                aria-selected={row.on}
                                className={cls}
                                data-hi={row.isHi ? '1' : '0'}
                                data-location-id={row.id}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => picker.pick(row.id)}
                            >
                                <WikiIcon
                                    name={row.isSub ? 'folder' : (row.icon || 'folder')}
                                    size={14}
                                    style={{ color: row.isSub ? 'var(--ink-3)' : (row.color || 'var(--ink-2)') }}
                                />
                                <span className="wk-loc-row-label">{row.text}</span>
                                {row.pathLabel && <span className="wk-loc-row-path">{row.pathLabel}</span>}
                                {row.on && <WikiIcon name="check" size={14} className="wk-loc-check" />}
                            </li>
                        );
                    })}
                    {rows.length === 0 && (
                        <li className="wk-loc-empty wk-t-meta" role="presentation" data-testid="wiki-location-empty">
                            {WIKI_MODAL_COPY.pickerEmpty}
                        </li>
                    )}
                </ul>
                <div className="wk-loc-foot" aria-hidden="true">{WIKI_MODAL_COPY.pickerHint}</div>
            </div>
        </>
    );
}
